import { ref, computed } from 'vue'
import fallbackAvatar from '@/assets/brand/default-avatar.webp'
import { request } from '@/api/userAuth'

export const siteConfig = ref(null)
export const siteError = ref('')
export const siteVerified = ref(false)
export const siteName = computed(() => siteConfig.value?.name || '')
const api = new URL(process.env.VUE_APP_USER_API_URL || '/api/user', window.location.origin)
const mediaUrl = (path) => /^\/media\/files\/(?:[a-f0-9]{16}|[a-f0-9]{48})(?:\.[a-z0-9]{1,12})?$/.test(path || '') ? new URL(path, api).href : ''
const failedLogo = ref('')
const processedImage = (path, size) => {
  const original = mediaUrl(path)
  return original && /\.(?:png|jpe?g|webp)$/i.test(path)
    ? original + '?x-oss-process=image/resize,m_lfit,' + size : original
}
const failedAvatar = ref('')
export const defaultAvatar = computed(() => (failedAvatar.value === siteConfig.value?.default_avatar ? mediaUrl(failedAvatar.value) : processedImage(siteConfig.value?.default_avatar, 'w_128,h_128')) || fallbackAvatar)
export function avatarError() { failedAvatar.value = siteConfig.value?.default_avatar || '' }
export const siteLogo = computed(() => siteConfig.value?.website_logo === failedLogo.value
  ? mediaUrl(failedLogo.value) : processedImage(siteConfig.value?.website_logo, 'w_640,h_320'))
export function siteLogoError(event) {
  if (event.target.src === siteLogo.value) failedLogo.value = siteConfig.value?.website_logo || ''
}
let iconGeneration = 0
let routeTitle = ''
let isHome = false
const cacheKey = 'site-branding-v1'
const encode = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
const decode = (value) => Uint8Array.from(atob(value), c => c.charCodeAt(0))

// Public presentation data only. Encryption avoids plain-text storage, not browser-owner access.
async function cacheCipher() {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`site-branding:${window.location.origin}`))
  return crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['encrypt', 'decrypt'])
}

export function updateSiteTitle(title = routeTitle, home = isHome) {
  routeTitle = title
  isHome = home
  const pageTitle = home ? (siteConfig.value?.homepage_title?.trim() || title || '首页') : title
  document.title = [pageTitle, siteName.value].filter(Boolean).join(' - ')
}

function apply(config) {
  siteConfig.value = config
  updateSiteTitle()
  let icon = document.querySelector('link[rel="icon"]')
  if (!icon) { icon = document.createElement('link'); icon.rel = 'icon'; document.head.appendChild(icon) }
  const currentIcon = ++iconGeneration
  const originalIcon = mediaUrl(config?.website_icon) || 'data:,'
  const optimizedIcon = processedImage(config?.website_icon, 'w_64,h_64') || originalIcon
  if (optimizedIcon === originalIcon) icon.href = originalIcon
  else {
    const image = new Image()
    image.onload = () => { if (currentIcon === iconGeneration) icon.href = optimizedIcon }
    image.onerror = () => { if (currentIcon === iconGeneration) icon.href = originalIcon }
    image.src = optimizedIcon
  }
  let description = document.querySelector('meta[name="description"]')
  if (!description) { description = document.createElement('meta'); description.name = 'description'; document.head.appendChild(description) }
  description.content = config?.homepage_description || ''
}

export async function initializeSite() {
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null')
    if (cached) {
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: decode(cached.iv) }, await cacheCipher(), decode(cached.data))
      const config = JSON.parse(new TextDecoder().decode(plain))
      if (config.expires > Date.now()) apply(config.site)
    }
  } catch { /* A missing or damaged cache never prevents loading current configuration. */ }
  try {
    const config = await request('site-config')
    apply(config)
    try {
      const iv = crypto.getRandomValues(new Uint8Array(12))
      const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await cacheCipher(), new TextEncoder().encode(JSON.stringify({ site: config, expires: Date.now() + 86400000 })))
      localStorage.setItem(cacheKey, JSON.stringify({ iv: encode(iv), data: encode(data) }))
    } catch { /* Storage may be disabled. */ }
    siteVerified.value = true
    return true
  } catch (error) {
    apply(null)
    try { localStorage.removeItem(cacheKey) } catch { /* Storage may be disabled. */ }
    siteError.value = error.code === 'SITE_UNAVAILABLE' ? '当前站点暂不可用' : '加载失败，请刷新重试'
    return false
  }
}

export const siteReady = initializeSite()
